import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { ArrowDownTrayIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import StatCard from '../../components/StatCard';
import api from '../../utils/api';

const C = { teal:'#00B4D8', success:'#10B981', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', error:'#EF4444', purple:'#8B5CF6' };
const DONUT_COLORS = [C.teal, C.purple, C.success, '#F59E0B', '#EF4444'];

export default function SuperAdminRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/superadmin/revenue')
      .then(res => setData(res.data))
      .catch(e => setError('Failed to load revenue data: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, []);

  const mrr = data?.mrr || 0;
  const tier1Mrr = data?.tier1_mrr || 0;
  const tier2Mrr = data?.tier2_mrr || 0;
  const collected = data?.collected || {};
  const outstanding = data?.outstanding || 0;
  const tenants = data?.tenants || [];
  const donutData = tenants.map(t => ({ name: t.name, value: t.monthly_fee_usd || 0 })).filter(d => d.value > 0);

  return (
    <Layout role="superadmin">
      {error && (
        <div style={{ background:'#FEE2E2', color:C.error, padding:'12px 16px', borderRadius:8, marginBottom:20 }}>{error}</div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20, marginBottom:24 }}>
        <StatCard title="Total MRR" value={loading ? null : mrr} prefix="$" icon={CurrencyDollarIcon} color={C.success} loading={loading} />
        <StatCard title="Tier 1 MRR" value={loading ? null : tier1Mrr} prefix="$" icon={CurrencyDollarIcon} color={C.teal} loading={loading} />
        <StatCard title="Tier 2 MRR" value={loading ? null : tier2Mrr} prefix="$" icon={CurrencyDollarIcon} color={C.purple} loading={loading} />
      </div>

      {/* Collected revenue by period */}
      <div className="card" style={{ padding:24, marginBottom:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:8, marginBottom:4 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Collected Revenue</h3>
          <span style={{ fontSize:13, color:C.grey }}>Outstanding (unpaid invoices): <strong style={{ color:C.error }}>${Number(outstanding).toLocaleString()}</strong></span>
        </div>
        <p style={{ margin:'0 0 18px', fontSize:13, color:C.grey }}>Confirmed partner subscription payments actually received. MRR above is contracted run-rate.</p>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:16 }}>
          {[
            ['This month', collected.this_month],
            ['Last month', collected.last_month],
            ['This year',  collected.this_year],
            ['Last year',  collected.last_year],
            ['All time',   collected.all_time],
          ].map(([label, value]) => (
            <div key={label} style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', background: label==='All time' ? '#F0FDF4' : '#fff' }}>
              <div style={{ fontSize:12, color:C.grey, fontWeight:600, marginBottom:6 }}>{label}</div>
              <div style={{ fontSize: label==='All time' ? 22 : 18, fontWeight:800, color: (value||0) > 0 ? C.success : C.grey }}>
                {loading ? '…' : `$${Number(value || 0).toLocaleString()}`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding:60, textAlign:'center', color:C.grey }}>Loading revenue data...</div>
      ) : tenants.length === 0 ? (
        <div className="card" style={{ padding:60, textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>💰</div>
          <div style={{ fontSize:18, fontWeight:600, color:'#374151', marginBottom:6 }}>No revenue data yet</div>
          <div style={{ fontSize:14, color:C.grey }}>Revenue will appear here once you have active partners with monthly fees set.</div>
        </div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
            {donutData.length > 0 ? (
              <div className="card" style={{ padding:24 }}>
                <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Revenue by Partner</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value">
                      {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                    </Pie>
                    <Legend />
                    <Tooltip formatter={v => [`$${Number(v).toFixed(0)}`, '']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="card" style={{ padding:24, display:'flex', alignItems:'center', justifyContent:'center', color:C.grey }}>
                No revenue data to chart.
              </div>
            )}
            <div className="card" style={{ padding:24 }}>
              <h3 style={{ margin:'0 0 20px', fontSize:15, fontWeight:700 }}>Partner Revenue Breakdown</h3>
              {tenants.filter(t => (t.monthly_fee_usd||0) > 0).length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={tenants.filter(t => (t.monthly_fee_usd||0) > 0)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis type="number" tick={{ fontSize:12 }} tickFormatter={v => `$${v}`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize:12 }} width={130} />
                    <Tooltip formatter={v => [`$${Number(v).toFixed(0)}`, 'Monthly Fee']} />
                    <Bar dataKey="monthly_fee_usd" fill={C.teal} radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:220, color:C.grey, fontSize:14 }}>
                  No partners with fees set.
                </div>
              )}
            </div>
          </div>

          <div className="card" style={{ overflow:'hidden' }}>
            <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Partner Revenue Table</h3>
              <button style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                <ArrowDownTrayIcon style={{ width:14, height:14 }} /> Export CSV
              </button>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#F9FAFB' }}>
                {['Partner','Plan','Tier','Monthly Fee','Status'].map(h => <th key={h} style={{ padding:'12px 20px', textAlign:'left', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {tenants.map((t, i) => (
                  <tr key={t.id || i} style={{ borderTop:`1px solid ${C.border}` }}>
                    <td style={{ padding:'14px 20px', fontWeight:600 }}>{t.name}</td>
                    <td style={{ padding:'14px 20px', textTransform:'capitalize', color:C.grey }}>{t.plan || '—'}</td>
                    <td style={{ padding:'14px 20px', color:C.grey }}>Tier {t.tier || 1}</td>
                    <td style={{ padding:'14px 20px', fontWeight:700, color:C.success }}>${(t.monthly_fee_usd||0).toFixed(0)}/mo</td>
                    <td style={{ padding:'14px 20px' }}>
                      <span style={{
                        fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                        background: (t.status||'active')==='active' ? '#D1FAE5' : '#FEF3C7',
                        color: (t.status||'active')==='active' ? C.success : '#92400E'
                      }}>{t.status || 'active'}</span>
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop:`2px solid ${C.border}`, background:'#F9FAFB' }}>
                  <td style={{ padding:'14px 20px', fontWeight:700 }} colSpan={3}>Total MRR</td>
                  <td style={{ padding:'14px 20px', fontWeight:700, color:C.success }}>${mrr.toFixed(0)}/mo</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
}
